import React from "react";
import type { DesignPreviewPage } from "campaign-builder/design/useDesignUpload";
import { getFlyerFormatMeta } from "./formatMeta";

// Generalized from the ADM aspect preview: aspect ratio + dimension labels come from
// the shared flyer format meta (millimetres) so it serves Tearaway / Door Hanger / A*.

interface DesignAspectPreviewProps {
  pages: DesignPreviewPage[];
  format: string;
  /** Account country — drives the dimension unit (US/CA inches, else mm). */
  countryCode?: string | null;
}

const DIM = "#90A1B9"; // slate-400

const dimLabel = (mm: number, countryCode?: string | null): string =>
  countryCode === "US" || countryCode === "CA" ? `${(mm / 25.4).toFixed(2)}"` : `${mm} mm`;

const sideLabel = (index: number, total: number) => (index === 0 ? "Front" : total > 1 ? "Back" : "");

// Cut/safe lines preserved from the legacy preview (ADMPDFPreviewer/styles.js):
// gray trim border, #AEAFC7 bleed lines inset 10px, green dashed safe area inset 18px.
const BleedAndSafeLines = () => (
  <div className="pointer-events-none absolute inset-0 z-10">
    <div className="absolute left-[10px] top-0 h-full w-px bg-[#AEAFC7]" />
    <div className="absolute right-[10px] top-0 h-full w-px bg-[#AEAFC7]" />
    <div className="absolute left-0 top-[10px] h-px w-full bg-[#AEAFC7]" />
    <div className="absolute bottom-[10px] left-0 h-px w-full bg-[#AEAFC7]" />
    <div
      className="absolute border border-dashed border-[#23BB32]"
      style={{ top: 18, left: 18, width: "calc(100% - 36px)", height: "calc(100% - 36px)" }}
    />
  </div>
);

// The uploaded/selected design placed inside a container with the selected format's
// aspect ratio (top-left aligned), with the cut/safe lines. Shared by the upload
// review and the gallery preview.
export const DesignFormatFrame = ({ dataUrl, format, alt }: { dataUrl: string; format: string; alt?: string }) => {
  const meta = getFlyerFormatMeta(format);
  const width = meta?.widthMm ?? 1;
  const height = meta?.heightMm ?? 1;
  return (
    <div
      className="relative border border-solid border-[#808080] bg-white"
      style={{ aspectRatio: `${width} / ${height}` }}
    >
      <img src={dataUrl} alt={alt ?? ""} className="absolute inset-0 h-full w-full object-contain object-left-top" />
      <BleedAndSafeLines />
    </div>
  );
};

// Vertical dimension line; the label sits centered on the line with a white
// background so it appears to "cut" through it.
const HeightIndicator = ({ label }: { label: string }) => (
  <div className="relative w-px self-stretch" style={{ backgroundColor: DIM }}>
    <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap bg-background px-1 py-1 text-xs font-medium text-[#90A1B9]">
      {label}
    </span>
  </div>
);

// Horizontal dimension line; the label sits centered on the line with a white
// background so it appears to "cut" through it.
const WidthIndicator = ({ label }: { label: string }) => (
  <div className="relative mt-3 h-px w-full" style={{ backgroundColor: DIM, gridColumnStart: 2 }}>
    <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-1.5 text-xs font-medium text-[#90A1B9]">
      {label}
    </span>
  </div>
);

export const DesignAspectPreview: React.FC<DesignAspectPreviewProps> = ({ pages, format, countryCode }) => {
  const meta = getFlyerFormatMeta(format);
  const widthLabel = meta ? dimLabel(meta.widthMm, countryCode) : "";
  const heightLabel = meta ? dimLabel(meta.heightMm, countryCode) : "";

  return (
    <div className="flex flex-col gap-10">
      {pages.map((page, index) => (
        <div key={page.pageNumber} className="grid grid-cols-[auto_1fr] gap-x-3">
          <span className="mb-2 text-sm font-medium leading-4 text-[#020618]" style={{ gridColumn: "1 / -1" }}>
            {sideLabel(index, pages.length)}
          </span>

          <HeightIndicator label={heightLabel} />
          <DesignFormatFrame dataUrl={page.dataUrl} format={format} alt={sideLabel(index, pages.length)} />

          <WidthIndicator label={widthLabel} />
        </div>
      ))}
    </div>
  );
};
